import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Params, Router } from '@angular/router';
import { InterceptorSkipHeader } from '@app/core/interceptors/authorization.interceptor';
import { canViewSolutionsProfileDetails } from '@app/+solutions/shared/utils/solutions-profile-access.util';
import { UserService } from '@app/shared/services/user.service';
import {
  IPeopleProfile,
  IPeopleProfileResponse,
  PeopleFilterType,
} from '../interfaces/solutions-profile.interface';
import { IPersonDetailResponse } from '../interfaces/person-detail.interface';
import {
  IAllProductsResponse,
  IProduct,
} from '@app/+products/shared/interfaces/product.interface';
import { normalizeSolutionsPeopleSort } from '@app/+solutions/shared/constants/solutions-people-sort-items';
import { normalizePeopleProfile } from '@app/+solutions/shared/utils/normalize-people-profile.util';
import { normalizePersonDetailResponse } from '@app/+solutions/shared/utils/normalize-person-detail.util';

function normalizePersonProductsResponse(
  res: IAllProductsResponse | IProduct[] | null | undefined
): { data: IProduct[]; count: number } {
  if (Array.isArray(res)) {
    return { data: res, count: res.length };
  }
  const data = res?.data ?? [];
  return { data, count: res?.count ?? data.length };
}

@Injectable()
export class SolutionsService {
  public profilesList = signal<IPeopleProfile[]>([]);
  public profilesCount = signal<number>(0);
  public isProfilesLoading = signal<boolean>(false);

  /** Current person detail (job_information + portfolio) for the portfolio page. */
  public personDetail = signal<IPersonDetailResponse | null>(null);
  public isPersonDetailLoading = signal<boolean>(false);

  /** Supplier products for the current person detail page. */
  public personProducts = signal<IProduct[]>([]);
  public personProductsCount = signal<number>(0);
  public isPersonProductsLoading = signal<boolean>(false);

  private readonly _httpClient = inject(HttpClient);
  private readonly _router = inject(Router);
  private readonly _userService = inject(UserService);

  // GET profiles from main/people with optional type (INSTALLERS | PROJECT_OWNERS | ARCHITECTS | SUPPLIERS).

  public getProfiles(params: Params & { type?: PeopleFilterType }) {
    this.profilesList.set([]);
    this.isProfilesLoading.set(true);

    const omitFromHttp = new Set(['page', 'perPage']);
    const query: Record<string, string | number | boolean> = {};
    for (const key of Object.keys(params)) {
      if (omitFromHttp.has(key)) continue;
      const v = params[key];
      if (v === undefined || v === null || v === '') continue;
      if (key === 'sort' && typeof v === 'string') {
        query[key] = normalizeSolutionsPeopleSort(v);
        continue;
      }
      query[key] = v as string | number | boolean;
    }

    return this._httpClient
      .get<IPeopleProfileResponse>(`main/people`, {
        params: query,
      })
      .subscribe({
        next: res => {
          const normalized = (res.data ?? [])
            .map(item => normalizePeopleProfile(item))
            .filter((item): item is IPeopleProfile => item != null);
          this.profilesList.set(normalized);
          this.profilesCount.set(res.count ?? 0);
        },
        error: () => this.isProfilesLoading.set(false),
        complete: () => this.isProfilesLoading.set(false),
      });
  }

  /** GET main/people/{id} – person detail (job_information + portfolio). */
  public getPersonDetail(id: string) {
    this.personDetail.set(null);
    this.isPersonDetailLoading.set(true);

    const canViewDetails = canViewSolutionsProfileDetails(
      this._userService.userSignal()
    );
    const requestOptions = canViewDetails
      ? {}
      : { headers: new HttpHeaders().set(InterceptorSkipHeader, '') };

    return this._httpClient
      .get<
        IPersonDetailResponse & { data?: IPersonDetailResponse }
      >(`main/people/${id}`, requestOptions)
      .subscribe({
        next: res =>
          this.personDetail.set(normalizePersonDetailResponse(res ?? null)),
        error: err => {
          this.isPersonDetailLoading.set(false);
          if (err?.status === 404) {
            this._router.navigate(['/404']);
          }
        },
        complete: () => this.isPersonDetailLoading.set(false),
      });
  }

  /** GET main/people/{id}/products – supplier product catalog for person detail. */
  public getPersonProducts(id: string) {
    this.personProducts.set([]);
    this.personProductsCount.set(0);
    this.isPersonProductsLoading.set(true);

    return this._httpClient
      .get<IAllProductsResponse | IProduct[]>(`main/people/${id}/products`)
      .subscribe({
        next: res => {
          const { data, count } = normalizePersonProductsResponse(res ?? null);
          this.personProducts.set(data);
          this.personProductsCount.set(count);
        },
        error: () => this.isPersonProductsLoading.set(false),
        complete: () => this.isPersonProductsLoading.set(false),
      });
  }
}
